import { useTranslations } from '@vocab/react';
import { Box, Heading, Stack, Pagination } from 'braid-design-system';
import { useLocation } from 'react-router-dom';

import GroupCard from 'src/components/atoms/GroupCard/GroupCard';
import { getPagePaths } from 'src/core/domain/pages';
import type { CategoryGroupsLoaderData } from 'src/mapper/groupMapper';
import { useTrackPageView } from 'src/provider/analytics';
import { useConfig } from 'src/provider/config';
import { useGroup } from 'src/provider/group';
import { usePage } from 'src/provider/page';

import translations from './.vocab';
import MyGroupEmptyList from './MyGroupEmptyList';

const useGetPageTitle = (): string => {
  const { countryCode } = useConfig();
  const {
    pageState: { pageName },
  } = usePage();
  const { t } = useTranslations(translations);

  switch (pageName) {
    case 'CommunityTopGroups':
      return t(`top-${countryCode}-groups`);
    case 'CommunityMyGroups':
      return t('my-groups');
    default:
      return t(`explore-${countryCode}-groups`);
  }
};

const useGetGroupList = (): {
  currentPage: CategoryGroupsLoaderData[];
  totalPage: number;
} => {
  const {
    groupState: {
      currentPageCategory,
      totalPage,
      myCategoryTotalPage,
      myCurrentPageCategory,
    },
  } = useGroup();
  const {
    pageState: { pageName },
  } = usePage();

  const isMyGroupPage = pageName === 'CommunityMyGroups';

  return {
    currentPage: isMyGroupPage ? myCurrentPageCategory : currentPageCategory,
    totalPage: isMyGroupPage ? myCategoryTotalPage : totalPage,
  };
};

export default () => {
  const title = useGetPageTitle();
  const {
    groupState: { sideBarMyGroupCategory },
    groupDispatch,
  } = useGroup();
  const { currentPage, totalPage } = useGetGroupList();
  const {
    pageState: { pageNumber, pageName },
    pageDispatch,
  } = usePage();
  const { t } = useTranslations(translations);
  const { language } = useConfig();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  useTrackPageView();
  const getLinkProps = ({ page }: { page: number }) => ({
    href: `${
      getPagePaths({
        language,
        queryParams: {
          type: searchParams.get('type') || '',
          page: page.toString(),
        },
      }).CommunityGroups
    }`,
    onClick: () => {
      groupDispatch({ type: 'PAGE_CHANGE', payload: { pageNumber: page } });
      pageDispatch({ type: 'SET_PAGE_NUMBER', payload: { pageNumber: page } });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });
  const isMyGroupPage = pageName === 'CommunityMyGroups';

  if (isMyGroupPage && sideBarMyGroupCategory.length === 0) {
    return <MyGroupEmptyList />;
  }
  return (
    <Box display="flex" flexDirection="column">
      <Box paddingBottom="large" paddingRight="small" paddingTop="medium">
        <Heading level="3" weight="regular">
          {title}
        </Heading>
      </Box>
      <Stack space="medium">
        {currentPage.map((group) => (
          <GroupCard key={`group-card-${group.categoryId}`} group={group} />
        ))}
      </Stack>
      {totalPage > 1 && pageNumber > 0 && (
        <Box paddingY="xxlarge">
          <Pagination
            page={pageNumber}
            total={totalPage}
            linkProps={getLinkProps}
            label={t('explore-groups')}
            nextLabel={t('next')}
            previousLabel={t('previous')}
          />
        </Box>
      )}
    </Box>
  );
};
